// eslint-disable-next-line no-restricted-imports
import * as Sentry from '@sentry/nextjs'; // only use as type so it's not included in the bundle
import getConfig from 'next/config';
import { getOptions } from './sentryOptions';
const { publicRuntimeConfig } = getConfig();

type CaptureExceptionParams = Parameters<typeof Sentry.captureException>;
type CaptureMessageParams = Parameters<typeof Sentry.captureMessage>;
type AddBreadcrumbParams = Parameters<typeof Sentry.addBreadcrumb>;

type State = {
  Sentry?: typeof Sentry;
  queue: (
    | { name: 'captureException'; params: CaptureExceptionParams }
    | { name: 'captureMessage'; params: CaptureMessageParams }
    | { name: 'addBreadcrumb'; params: AddBreadcrumbParams }
  )[];
};

const state: State = {
  queue: [],
};

export const onLoaded = async () => {
  const Sentry = await import('@sentry/nextjs');
  state.Sentry = Sentry;

  Sentry.init(
    getOptions({
      dsn: publicRuntimeConfig.SENTRY_DSN,
      environment: publicRuntimeConfig.ENV,
      release: process.env.SENTRY_RELEASE,
      maxBreadcrumbs: 50,
      attachStacktrace: true,
      tracesSampler: ({ location }) => {
        if (location?.pathname === '/') {
          return 1;
        }
        return 0;
      },
    }),
  );

  Sentry.configureScope((scope: Sentry.Scope) => {
    scope.setTag('ssr', 'false');
  });

  state.queue.forEach((item) => {
    switch (item.name) {
      case 'addBreadcrumb':
        addBreadcrumb(...item.params);
        return;
      case 'captureException':
        captureException(...item.params);
        return;
      case 'captureMessage':
        captureMessage(...item.params);
        return;
    }
  });
};

export const captureException = async (...params: CaptureExceptionParams) => {
  const { Sentry } = state;
  if (Sentry) {
    return Sentry.withScope((scope) => {
      const [err] = params;

      if (err.message) {
        // De-duplication currently doesn't work correctly for SSR / browser errors
        // so we force deduplication by error message if it is present
        scope.setFingerprint([err.message]);
      }

      if (err.statusCode) {
        scope.setExtra('statusCode', err.statusCode);
        if (err.statusCode == 404) {
          scope.setLevel(Sentry.Severity.Warning);
        }
      }

      Sentry.captureException(...params);
    });
  }
  state.queue.push({ name: 'captureException', params });
};
export const captureMessage = (...params: CaptureMessageParams) => {
  const { Sentry } = state;
  if (Sentry) {
    return Sentry.captureMessage(...params);
  }
  state.queue.push({ name: 'captureMessage', params });
};
export const addBreadcrumb = (...params: AddBreadcrumbParams) => {
  const { Sentry } = state;
  if (Sentry) {
    return Sentry.addBreadcrumb(...params);
  }
  state.queue.push({ name: 'addBreadcrumb', params });
};
